class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initializeUpsellModule = this.initializeUpsellModule.bind(this);
    document.addEventListener('helium:cart_updated', () => {
      setTimeout(this.initializeUpsellModule, 50);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.initializeUpsellModule();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.initializeUpsellModule();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  initializeUpsellModule() {
    const module = this.querySelector('[data-cart-drawer-upsell]');
    if (!module || module.dataset.initialized === 'true') return;

    module.dataset.initialized = 'true';
    const items = Array.from(module.querySelectorAll('[data-upsell-item]'));
    const statusRegion = module.querySelector('[data-upsell-status]');

    const pushAnalytics = (eventName, item) => {
      if (!item) return;
      const payload = {
        event: eventName,
        context: 'cart_drawer',
        source: 'upsell_module',
        product_id: Number(item.dataset.productId || 0),
        variant_id: Number(item.dataset.variantId || 0),
        product_handle: item.dataset.productHandle || '',
        product_title: item.dataset.productTitle || '',
        position: Number(item.dataset.position || 0),
        recommendation_type: item.dataset.recommendationType || 'best_seller_fallback',
        currency: window.HeliumAnalyticsContext && window.HeliumAnalyticsContext.currency ? window.HeliumAnalyticsContext.currency : '',
        price: Number(item.dataset.price || 0),
        cart_token: module.dataset.cartToken || '',
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      document.dispatchEvent(new CustomEvent(`helium:${eventName}`, { detail: payload }));
    };

    const impressionObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const item = entry.target;
          if (item.dataset.impressionTracked === 'true') {
            observer.unobserve(item);
            return;
          }
          item.dataset.impressionTracked = 'true';
          pushAnalytics('cart_upsell_impression', item);
          observer.unobserve(item);
        });
      },
      { root: module.closest('cart-drawer-items'), threshold: 0.6 }
    );

    items.forEach((item) => {
      impressionObserver.observe(item);

      item.querySelectorAll('[data-upsell-product-link]').forEach((link) => {
        link.addEventListener('click', () => {
          pushAnalytics('cart_upsell_click', item);
        });
      });

      const button = item.querySelector('[data-upsell-add]');
      if (!button) return;
      button.addEventListener('click', async () => {
        if (button.classList.contains('is-loading')) return;

        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (statusRegion) statusRegion.textContent = `Adding ${item.dataset.productTitle} to your cart`;

        try {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [
                {
                  id: Number(item.dataset.variantId),
                  quantity: 1,
                },
              ],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });

          const responseText = await response.text();
          const parsedState = JSON.parse(responseText);

          if (parsedState.status || parsedState.errors) {
            throw new Error(parsedState.description || parsedState.message || 'Unable to add item');
          }

          pushAnalytics('cart_upsell_add_to_cart', item);
          if (statusRegion) statusRegion.textContent = `${item.dataset.productTitle} added to your cart`;
          this.renderContents(parsedState);
        } catch (error) {
          if (statusRegion) statusRegion.textContent = error.message || 'Unable to add this item right now';
        } finally {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
    });
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


